/* Xiaomi 2026 · Message Composer modal
 *
 * Flow (matches Dipali's "brain + post-box" doctrine):
 *   1. Ayush types English in the top textarea
 *   2. Picks one or more languages (dealer's `language_pref` pre-selected)
 *   3. Clicks "Translate" → ScaleBoard hits Claude Sonnet 4.5
 *   4. Black-box preview shows the translated message in target language
 *   5. "Save & send to Zoho" persists on the instance + (if Zoho ticket
 *      exists) posts as Zoho comment for the LSM to fire on WhatsApp
 *   6. If Zoho isn't live yet, status = "queued_local" · admin can
 *      flush later via /api/xiaomi/messages/flush-pending
 *
 * Props:
 *   instance        — full instance object (has _id, dealer_id, benefit_id,
 *                                            zoho_ticket_id, language_used)
 *   dealerLanguage  — dealer.language_pref (e.g. "te") · used to pre-select
 *   benefitName     — display string for the composer header
 *   onClose         — close modal
 *   onSaved         — fire after a successful save (parent refreshes)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import EditablePref from './EditablePref';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});


// Static fallback · matches `backend/services/xiaomi_message_translator.py`
const LANGUAGES = [
  { code: 'en', name: 'English',   endonym: 'English' },
  { code: 'hi', name: 'Hindi',     endonym: 'हिन्दी' },
  { code: 'te', name: 'Telugu',    endonym: 'తెలుగు' },
  { code: 'ta', name: 'Tamil',     endonym: 'தமிழ்' },
  { code: 'kn', name: 'Kannada',   endonym: 'ಕನ್ನಡ' },
  { code: 'bn', name: 'Bengali',   endonym: 'বাংলা' },
  { code: 'mr', name: 'Marathi',   endonym: 'मराठी' },
  { code: 'gu', name: 'Gujarati',  endonym: 'ગુજરાતી' },
  { code: 'pa', name: 'Punjabi',   endonym: 'ਪੰਜਾਬੀ' },
  { code: 'ml', name: 'Malayalam', endonym: 'മലയാളം' },
  { code: 'or', name: 'Odia',      endonym: 'ଓଡ଼ିଆ' },
];


export default function MessageComposerModal({
  instance, dealerId: dealerIdProp, dealerLanguage, benefitName,
  initialSource, initialTarget,
  // Optional editable preferences strip — when supplied, renders an
  // inline editor above the source textarea so Ayush can tweak the
  // dinner / weekend / holiday options live. Every save patches the
  // dealer doc + writes an audit entry. After save we call
  // rebuildSource() so the textarea reflects the new value.
  editablePrefs, onPrefSaved, rebuildSource,
  onClose, onSaved, onDraftReady,
}) {
  // "Dealer-level mode" = no real instance · we save the message at the
  // dealer level so Ayush can compose ANYTIME, even before workflows
  // materialise on Jun 1.
  const dealerId = dealerIdProp || instance?.dealer_id;
  const instanceId = instance?._id;
  const instanceIsReal = instanceId
    && !instanceId.startsWith('__draft_')
    && !instanceId.startsWith('__no_');
  const benefitContext = benefitName || (instance?.benefit_id
    || '').replace('xiaomi_', '').replace(/_/g, ' ');
  // Two-step UX: when `onDraftReady` is supplied (Quick Preview path),
  // the modal closes after Translate and the draft lands in the parent's
  // green WhatsApp box.  Without it (full 360 path), the modal saves
  // directly via "Save & send to Zoho".
  const twoStepMode = typeof onDraftReady === 'function';

  // Pre-select dealer's preferred language · fallback English
  const initialLang = LANGUAGES.find(
    (l) => l.code === (initialTarget || dealerLanguage || 'en')
  ) || LANGUAGES[0];

  const [sourceText, setSourceText] = useState(initialSource || '');
  // Live map of pref fieldPath → latest value · used by the editable
  // strip so rebuildSource() always sees freshly-saved edits.
  const prefValuesRef = useRef(
    (editablePrefs || []).reduce((acc, p) => ({ ...acc, [p.fieldPath]: p.value }), {}),
  );
  const [targetLang, setTargetLang] = useState(initialLang.code);
  const [customLang, setCustomLang] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [targetLangName, setTargetLangName] = useState(initialLang.name);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [autoPushZoho, setAutoPushZoho] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!dealerId) return;
    try {
      // Always load dealer-level history · covers BOTH ad-hoc and
      // instance-mirrored messages
      const r = await axios.get(
        `${API}/xiaomi/messages/dealer/${encodeURIComponent(dealerId)}`,
        auth(),
      );
      setHistory(r.data.messages || []);
    } catch (_e) {
      // non-blocking
    }
  }, [dealerId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const effectiveTarget = usingCustom ? customLang.trim() : targetLang;
  const effectiveTargetName = usingCustom
    ? (customLang.trim() || 'Custom')
    : (LANGUAGES.find((l) => l.code === targetLang)?.name || 'English');

  const doTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error('Type your English message first');
      return;
    }
    if (usingCustom && !customLang.trim()) {
      toast.error('Type the target language name');
      return;
    }
    setTranslating(true);
    setTranslatedText('');
    try {
      const r = await axios.post(`${API}/xiaomi/messages/translate`, {
        source_text: sourceText,
        target_language: effectiveTarget,
        context_hint: benefitContext
          ? `benefit: ${benefitContext} · partner outreach`
          : undefined,
        instance_id: instanceIsReal ? instanceId : undefined,
      }, auth());
      setTranslatedText(r.data.translated_text);
      setTargetLangName(r.data.target_language_name);
      toast.success(`Translated → ${r.data.target_language_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translation failed');
    } finally { setTranslating(false); }
  };

  const doSave = async () => {
    if (!sourceText.trim() || !translatedText.trim()) {
      toast.error('Translate the message before saving');
      return;
    }
    if (!dealerId) {
      toast.error('Missing dealer context');
      return;
    }
    setSaving(true);
    try {
      // Dealer-level mode whenever we don't have a real instance OR
      // when caller passed dealerId directly (Quick Preview path)
      const useDealerEndpoint = !instanceIsReal;
      const url = useDealerEndpoint
        ? `${API}/xiaomi/messages/dealer/${encodeURIComponent(dealerId)}`
        : `${API}/xiaomi/messages/instance/${encodeURIComponent(instanceId)}`;
      const body = {
        source_text: sourceText,
        translated_text: translatedText,
        target_language: effectiveTarget,
        target_language_name: targetLangName || effectiveTargetName,
        auto_push_to_zoho: autoPushZoho,
      };
      if (useDealerEndpoint) {
        body.instance_id = instanceIsReal ? instanceId : null;
        body.benefit_id = instance?.benefit_id || null;
        body.benefit_label = benefitContext || null;
      }
      const r = await axios.post(url, body, auth());
      const z = r.data.zoho || {};
      if (z.status === 'pushed') {
        toast.success('Saved · pushed to Zoho ticket');
      } else if (z.status === 'queued_local') {
        toast.success('Saved locally · Zoho ticket pending (will flush when ready)');
      } else {
        toast.success('Saved');
      }
      setSourceText('');
      setTranslatedText('');
      loadHistory();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="message-composer-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-start justify-between"
          style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)' }}>
          <div>
            <h2 className="text-xl" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
              Compose WhatsApp message
            </h2>
            <p className="text-[12px] text-soft mt-0.5">
              {benefitContext ? `${benefitContext} · ` : ''}
              {instanceIsReal ? (
                <span className="font-mono">{instanceId?.slice(0, 30)}…</span>
              ) : (
                <span className="text-[#7c2d12]">ad-hoc message · dealer-level</span>
              )}
              {instance?.zoho_ticket_id ? (
                <span className="ml-2 text-[#166534]">
                  · Zoho ticket #{instance.zoho_ticket_id} ready
                </span>
              ) : (
                <span className="ml-2 text-[#166534]">
                  · Will auto-open a Zoho ticket on send (Tanisha's team fires WhatsApp)
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose}
            className="text-soft hover:text-ink text-2xl"
            data-testid="composer-close-btn">×</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          {/* Editable preferences strip (optional · only when Ayush surface supplies it) */}
          {editablePrefs && editablePrefs.length > 0 && (
            <div
              className="rounded-lg border p-3 space-y-2"
              style={{ background: '#fffbf0', borderColor: '#fde68a' }}
              data-testid="composer-editable-prefs"
            >
              <div className="text-[11px] uppercase tracking-wider font-semibold flex items-center justify-between" style={{ color: '#9A4B0E' }}>
                <span>📋 Pre-curated options · click ✏ to edit (audit-logged)</span>
                {typeof rebuildSource === 'function' && (
                  <button
                    type="button"
                    onClick={() => setSourceText(rebuildSource(prefValuesRef.current))}
                    className="text-[10px] underline normal-case"
                    style={{ color: '#7c2d12' }}
                    data-testid="composer-rebuild-source"
                  >
                    🔄 Rebuild message from latest options
                  </button>
                )}
              </div>
              {editablePrefs.map((p) => (
                <EditablePref
                  key={p.fieldPath}
                  dealerId={dealerId}
                  fieldPath={p.fieldPath}
                  label={p.label}
                  value={prefValuesRef.current[p.fieldPath] ?? p.value}
                  onSaved={(newVal) => {
                    // Track latest values locally so rebuildSource sees them
                    prefValuesRef.current = { ...prefValuesRef.current, [p.fieldPath]: newVal };
                    // Inform parent so it can refresh its row state
                    onPrefSaved?.(p.fieldPath, newVal);
                    // Auto-rebuild source if helper available
                    if (typeof rebuildSource === 'function') {
                      setSourceText(rebuildSource(prefValuesRef.current));
                    }
                  }}
                  testId={`composer-pref-${p.fieldPath.replace(/\./g, '-')}`}
                />
              ))}
            </div>
          )}

          {/* English source */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-soft mb-1">
              Step 1 · type your English message
            </div>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              rows={5}
              placeholder={'Type the WhatsApp message you want to send.\nUse {{1}} {{2}} for variables (Gupshup template placeholders).\nEmoji and proper nouns are preserved during translation.'}
              className="w-full px-3 py-2 rounded-lg border border-line text-sm font-medium focus:outline-none focus:border-[#ff6900] resize-y"
              data-testid="composer-source-input"
            />
            <div className="text-[10px] text-soft mt-1">
              {sourceText.length} chars · placeholders + emoji preserved · proper nouns kept English
            </div>
          </div>

          {/* Language chips + custom */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-soft mb-1.5">
              Step 2 · target language
              {dealerLanguage && (
                <span className="ml-2 normal-case text-[10px] text-[#7c2d12]">
                  · dealer prefers <strong>{
                    (LANGUAGES.find((l) => l.code === dealerLanguage)?.name)
                    || dealerLanguage.toUpperCase()}</strong>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {LANGUAGES.map((l) => {
                const active = !usingCustom && targetLang === l.code;
                const preferred = l.code === dealerLanguage;
                return (
                  <button
                    key={l.code}
                    onClick={() => { setTargetLang(l.code); setUsingCustom(false); }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      active
                        ? 'bg-[#ff6900] text-white border-[#ff6900]'
                        : preferred
                        ? 'bg-[#fff7ed] text-[#7c2d12] border-[#fb923c]'
                        : 'bg-white text-[#15110d] border-line hover:bg-[#faf6ef]'
                    }`}
                    data-testid={`composer-lang-${l.code}`}
                  >
                    {l.name} <span className="opacity-70">{l.endonym}</span>
                    {preferred && !active && (
                      <span className="ml-1 text-[9px] uppercase tracking-wider">pref</span>
                    )}
                  </button>
                );
              })}
              {/* Custom language */}
              <button
                onClick={() => setUsingCustom(true)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                  usingCustom
                    ? 'bg-[#15110d] text-white border-[#15110d]'
                    : 'bg-white text-[#15110d] border-dashed border-soft hover:bg-[#faf6ef]'
                }`}
                data-testid="composer-lang-custom"
              >
                + Other…
              </button>
              {usingCustom && (
                <input
                  type="text"
                  value={customLang}
                  onChange={(e) => setCustomLang(e.target.value)}
                  placeholder="Assamese, Sindhi, Konkani…"
                  className="px-2.5 py-1 rounded-full text-[11px] border border-[#15110d] bg-white focus:outline-none focus:border-[#ff6900]"
                  data-testid="composer-lang-custom-input"
                />
              )}
            </div>
            <button
              onClick={doTranslate}
              disabled={translating || !sourceText.trim()}
              className="mt-3 px-5 py-2 rounded-lg bg-[#ff6900] text-white font-semibold text-sm hover:bg-[#cc5500] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="composer-translate-btn">
              {translating ? 'Translating with Claude Sonnet 4.5…' : '⟶ Translate'}
            </button>
          </div>

          {/* Black box preview */}
          {(translatedText || translating) && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-soft mb-1.5">
                Step 3 · preview ({targetLangName})
              </div>
              <div
                className="rounded-xl p-5 text-[#fefefe] text-sm whitespace-pre-wrap leading-relaxed"
                style={{ background: '#15110d', minHeight: '120px',
                          fontFamily: 'ui-monospace, "SF Mono", "Cascadia Code", monospace',
                          fontSize: '13px' }}
                data-testid="composer-black-box"
              >
                {translating ? (
                  <span className="opacity-60 italic">⟳ translating…</span>
                ) : (
                  translatedText
                )}
              </div>
              {translatedText && (
                <div className="text-[10px] text-soft mt-1">
                  {translatedText.length} chars · model claude-sonnet-4-5
                </div>
              )}
            </div>
          )}

          {/* Save controls */}
          {translatedText && (
            <div className="pt-3 border-t border-line">
              {twoStepMode ? (
                <>
                  <button onClick={() => onDraftReady({
                      source_text: sourceText,
                      translated_text: translatedText,
                      target_language: effectiveTarget,
                      target_language_name: targetLangName || effectiveTargetName,
                    })}
                    className="px-6 py-2.5 rounded-lg bg-[#ff6900] text-white font-semibold text-sm hover:bg-[#cc5500] flex items-center gap-2"
                    data-testid="composer-insert-draft-btn">
                    → Insert into preview
                  </button>
                  <p className="text-[10px] text-soft mt-1.5">
                    Draft lands in the green WhatsApp box on the right.
                    Review it in context, then press <strong>🚀 Send to Zoho</strong>.
                  </p>
                </>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-[12px] text-[#15110d] mb-2">
                    <input type="checkbox" checked={autoPushZoho}
                      onChange={(e) => setAutoPushZoho(e.target.checked)}
                      data-testid="composer-auto-push-zoho" />
                    Auto-push to Zoho ticket (Tanisha / LSM fires from there)
                  </label>
                  <button onClick={doSave} disabled={saving}
                    className="px-6 py-2.5 rounded-lg bg-[#15803d] text-white font-semibold text-sm hover:bg-[#166534] disabled:opacity-50"
                    data-testid="composer-save-btn">
                    {saving ? 'Saving…' : '💾 Save & send to Zoho'}
                  </button>
                  <p className="text-[10px] text-soft mt-1.5">
                    ScaleBoard saves a permanent copy on this ticket.
                    {' '}If Zoho is not yet live, the message queues locally and
                    flushes automatically once the bridge activates.
                  </p>
                </>
              )}
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="pt-3 border-t border-line">
              <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
                Past messages on this benefit · {history.length}
              </div>
              <ul className="space-y-2 max-h-[260px] overflow-y-auto"
                data-testid="composer-history-list">
                {history.map((m) => (
                  <li key={m._id} className="rounded-lg border border-line p-2.5 text-[12px]">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div>
                        <span className="font-semibold text-[#15110d]">
                          {m.target_language_name}
                        </span>
                        <span className="text-[10px] text-soft ml-2">
                          {m.composed_by} · {new Date(m.composed_at).toLocaleString()}
                        </span>
                      </div>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        m.zoho_push_status === 'pushed'
                          ? 'bg-[#dcfce7] text-[#166534]'
                          : 'bg-[#fef3c7] text-[#92400e]'
                      }`}>
                        {m.zoho_push_status === 'pushed' ? '✓ in zoho' : 'queued'}
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap text-[#374151] bg-[#faf6ef] rounded p-2">
                      {m.translated_text}
                    </div>
                    <details className="mt-1">
                      <summary className="text-[10px] text-soft cursor-pointer hover:text-ink">
                        English source
                      </summary>
                      <div className="text-[11px] text-soft mt-1 whitespace-pre-wrap">
                        {m.source_text}
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
